import {Injectable, signal} from '@angular/core';
import {MapResource} from '../resource/map.resource';
import {
  clearSelectedMap,
  GraphMap,
  GraphNode,
  GraphNodeLink,
  GraphNodeMapListKey,
  GroupedTemplates,
  readSelectedMap,
  writeSelectedMap,
} from '../interfaces/graph-node.interface';
import {Observable, Subject, tap} from 'rxjs';
import {UserService} from './user.service';
import {NodePropertiesDto, OtherLinksMode, OtherNodesMode, SmapletComments} from '@maporium-workspace/shared';
import {isInIframe} from '../../shared/helpers/maporium.validators';
import {get, isEmpty, set} from 'lodash';
import {ActivatedRoute} from '@angular/router';
import {LinkViewMode} from './setting.service';
import {MapUserRoles} from '../../acl/map-user-roles.enum';
import {Job} from '../interfaces/jobs.dto';

@Injectable({ providedIn: 'root' })
export class MapService {

  /**
   * @deprecated TODO: Rework to signal
   */
  public mapChangedSubject = new Subject<GraphMap | undefined>();
  public openMapSheetSubject = new Subject<void>();
  /**
   * @deprecated TODO: Rework to signal
   */
  public mapNameChangedSubject = new Subject<string>();
  public sidebarUpdatingSubject = new Subject<boolean>();
  public readonly selectedMap = signal<GraphMap | undefined>(this.getCurrentSelectedMapFromStore());
  public readonly mapNameChangedSignal = signal<string>(this.selectedMap()?.name || '');

  private reconnectInterval: any;

  constructor(private resource: MapResource,
              private userService: UserService,
              private route: ActivatedRoute) {
  }

  /**
   * Updates the map name locally without making a server request
   * Use this to provide immediate UI feedback while waiting for server update
   */
  updateMapNameLocally(map: GraphMap, newName: string): void {
    if (!map) return;

    // Update the current map name locally
    const updatedMap = { ...map, name: newName };

    // Update map in the store
    this.setCurrentSelectedMapToStore(updatedMap);

    // Notify other components about the name change
    this.mapNameChangedSubject.next(newName);
  }

  getSelectedMapSignal(): GraphMap | undefined {
    return this.selectedMap();
  }

  getMapNameSignal(): string {
    return this.mapNameChangedSignal();
  }

  setMapNameSignal(name: string): void {
    this.mapNameChangedSignal.set(name);
  }

  setCurrentSelectedMapToStore(map: GraphMap | undefined) {
    if (map) {
      const existingList = JSON.parse(localStorage.getItem(GraphNodeMapListKey) || '{}');
      if (!isEmpty(existingList)) {
        set(existingList, map.id, map);
        localStorage.setItem(GraphNodeMapListKey, JSON.stringify(existingList));
      } else {
        localStorage.setItem(GraphNodeMapListKey, JSON.stringify({ [map.id]: map }));
      }
      if (!isInIframe()) {
        writeSelectedMap(JSON.stringify(map));
      }
    } else {
      if (!isInIframe()) {
        clearSelectedMap();
      }
    }
    this.cleanupTransferConnection();
    this.selectedMap.set(map);
  }


  /**
   * Reads the current user from local storage and checks if the user is the author of the current local storage map
   */
  isSmapAuthor(): boolean {
    return this.userService.getCurrentUserFromStorage().id === this.selectedMap()?.owner.id;
  }

  removeCurrentSelectedMapFromStore(mapId?: string) {
    if (!mapId) {
      clearSelectedMap();
    }
  }

  getAllToursAndViewsForMap(mapId: string): Observable<{ tours: any[], views: any[] }> {
    return this.resource.getAllToursAndViewsForMap(mapId);
  }

  getAiUsage(mapId: string): Observable<{
    aiCreditsUsed: number;
    aiUsageBySkill: Record<string, { credits: number; count: number; durationMs: number }>;
  }> {
    return this.resource.getAiUsage(mapId);
  }

  getCurrentSelectedMapFromStore(): GraphMap {
    const isIframe = isInIframe();
    const params = this.route.firstChild?.snapshot.params;

    const mapId = isIframe ? this.route.snapshot.queryParams['mapId'] : get(params, 'id', null);
    const storedMaps = localStorage.getItem(GraphNodeMapListKey);
    const storedSingleMap = readSelectedMap();
    if (this.hasNoStoredMap(mapId)) {
      return {} as GraphMap;
    } else {
      let storedMapObject = isIframe ? get(JSON.parse(storedMaps as string), mapId) : JSON.parse(storedSingleMap as string);
      if (storedMapObject?.map) {
        storedMapObject = { ...storedMapObject.map };
        delete storedMapObject.map;
        this.setCurrentSelectedMapToStore(storedMapObject);
      }
      return storedMapObject;
    }
  }

  static getIFramedMap(mapId: string): GraphMap {
    const storedMap = localStorage.getItem(GraphNodeMapListKey);
    return get(JSON.parse(storedMap as string), mapId);
  }

  findTemplates(): Observable<GroupedTemplates> {
    return this.resource.findTemplates();
  }

  findAllForUser(userId: string): Observable<GraphMap[]> {
    return this.resource.findAllForUser(userId);
  }

  findPublicMap(mapId: string): Observable<GraphMap> {
    return this.resource.publicGetMap(mapId);
  }

  findByIdWithRelation(mapsId: string): Observable<GraphMap> {
    return this.resource.findByIdWithRelation(mapsId)
      .pipe(
        tap((map) => {
          this.setCurrentSelectedMapToStore(map);
        })
      );
  }

  createForUser(map: Partial<GraphMap>): Observable<GraphMap> {
    return this.resource.createForUser(map);
  }

  createDefaultMap(map: Partial<GraphMap>): Observable<GraphMap> {
    return this.resource.createDefaultMap(map);
  }

  update(map: Partial<GraphMap>): Observable<GraphMap> {
    return this.resource.update(map);
  }

  delete(mapId: string) {
    return this.resource.delete(mapId);
  }

  duplicate(map: Partial<GraphMap>): Observable<GraphMap> {
    return this.resource.duplicate(map);
  }

  exportMapWithRelationsRecursive(graphMap: GraphMap): Observable<BlobPart> {
    return this.resource.exportMapWithRelationsRecursive(graphMap.id)
      .pipe(tap((map) => {
        const snakeCasedMapName = graphMap.name.replace(/ /g, '_').toLowerCase();
        const noSpecialCharactersMapName = snakeCasedMapName.replace(/[^a-zA-Z0-9]/g, '_');
        // @ts-ignore
        const url = window.URL.createObjectURL(map);
        const a = document.createElement('a');
        a.href = url;
        a.download = noSpecialCharactersMapName + '.smap';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }));
  }

  copyToMap(nodes: GraphNode[], mapId: string, grandParent?: GraphNode | null): Observable<GraphNode[]> {
    return this.resource.copyToMap(nodes, mapId, grandParent);
  }

  toggleGuestComments(mapId: string, hasGuestComments: boolean): Observable<GraphMap> {
    return this.resource.toggleGuestComments(mapId, hasGuestComments);
  }

  importMapFromZip(file: File): Observable<Job> {
    const formData = new FormData();
    formData.append('file', file);
    return this.resource.importMapFromZip(formData);
  }

  createGptMapWithNodeAndEdges(gptNodes: GptNode[], gptEdges: GptEdge[]) {
    const graphNodes = gptNodes.map((gptNode) => {
      const graphNode: GraphNode = {
        id: gptNode.id,
        name: gptNode.name,
        color: gptNode.color,
        properties: gptNode.properties,
        children: [],
        x: gptNode.position.x,
        y: gptNode.position.y,
        links: []
      };
      return graphNode;
    });
    const graphEdges = gptEdges.map((gptEdge) => {
      const graphEdge = {
        id: gptEdge.id,
        name: gptEdge.name,
        color: gptEdge.color,
        from: gptEdge.from,
        to: gptEdge.to
      };
      return graphEdge;
    });
    //@ts-ignore
    return this.resource.createGptMapWithNodeAndEdges(graphNodes, graphEdges, this.userService.getCurrentUserFromStorage().id);
  }

  createMapFromAiData(nodes: GraphNode[], links: GraphNodeLink[]) {
    const userId = this.userService.getCurrentUserFromStorage()?.id;
    // Build clean minimal node objects - exclude children to avoid tree closure table issues
    const cleanNodes = nodes.map((node: any) => ({
      id: node.id,
      name: node.name,
      color: node.color,
      properties: node.properties,
      x: node.x,
      y: node.y,
      ...(node.parentId ? {parent: {id: node.parentId}} : {})
    }));
    // Links need from/to as objects with id for the backend
    const cleanLinks = links.map((link) => ({
      id: link.id,
      name: link.name,
      color: link.color,
      from: {id: typeof link.from === 'string' ? link.from : (link.from as any)?.id},
      to: {id: typeof link.to === 'string' ? link.to : (link.to as any)?.id}
    }));
    return this.resource.createGptMapWithNodeAndEdges(cleanNodes as GraphNode[], cleanLinks as GraphNodeLink[], userId);
  }

  shareMap(mapId: string, usersList: { users: { id: string, role: MapUserRoles }[] }) {
    return this.resource.shareMap(mapId, usersList);
  }

  unshareMap(mapId: string) {
    return this.resource.unshareMap(mapId);
  }

  changeOwner(mapId: string, userId: string) {
    return this.resource.changeOwner(mapId, userId);
  }

  connectToTransferUpdates(mapId: string,
                           onMessage: (data: GraphMap) => void,
                           onError: (error: any) => void) {
    this.resource.connectToTransferSse(
      mapId,
      (data: GraphMap) => {
        onMessage(data);
      },
      (error) => {
        console.warn('Network disruption detected, reconnecting in 1min');

        // Retry connection on error
        this.reconnectInterval = setTimeout(
          () => this.connectToTransferUpdates(mapId, onMessage, onError),
          60000
        );
        onError(error);
      }
    );
  }

  hasNoStoredMap(mapId?: string): boolean {
    const storedMap = isInIframe() ? localStorage.getItem(GraphNodeMapListKey) : readSelectedMap();
    if (mapId && isInIframe()) {
      const iframeRelatedMap = get(JSON.parse(storedMap as string), mapId);
      return iframeRelatedMap === undefined || iframeRelatedMap === null || iframeRelatedMap === '' || iframeRelatedMap === 'undefined' || iframeRelatedMap === 'null';
    }
    return storedMap === undefined || storedMap === null || storedMap === '' || storedMap === 'undefined' || storedMap === 'null';
  }

  openPublicAccessToMap(mapId: string) {
    return this.resource.openPublicAccessToMap(mapId);
  }

  closePublicAccessToMap(mapId: string) {
    return this.resource.closePublicAccessToMap(mapId);
  }

  cleanupTransferConnection(): void {
    this.resource.closeSse();

    if (this.reconnectInterval) {
      clearTimeout(this.reconnectInterval);
      this.reconnectInterval = null;
    }
  }

  setLinkViewMode(mapId: string, mode: LinkViewMode) {
    return this.resource.setLinkViewMode(mapId, mode);
  }

  setOtherLinksMode(mapId: string, mode: OtherLinksMode) {
    return this.resource.setOtherLinksMode(mapId, mode);
  }

  setOtherNodesMode(mapId: string, mode: OtherNodesMode) {
    return this.resource.setOtherNodesMode(mapId, mode);
  }

  setSmapletOpening(mapId: string, opening: {storyId: string | null; sceneId: string | null; silent: boolean}) {
    return this.resource.setSmapletOpening(mapId, opening);
  }

  setSmapletView(mapId: string, view: '2d' | '3d') {
    return this.resource.setSmapletView(mapId, view);
  }

  setSmapletAutoOrbit(mapId: string, autoOrbit: boolean) {
    return this.resource.setSmapletAutoOrbit(mapId, autoOrbit);
  }

  setSmapletComments(mapId: string, comments: SmapletComments) {
    return this.resource.setSmapletComments(mapId, comments);
  }

  setAllowWebsiteEmbedding(mapId: string, allow: boolean) {
    return this.resource.setAllowWebsiteEmbedding(mapId, allow);
  }

  getStarterMap(): Observable<GraphMap | null> {
    return this.resource.getStarterMap();
  }

  getOrCreateStarterMap(): Observable<GraphMap> {
    return this.resource.getOrCreateStarterMap();
  }
}

export interface GptNode {
  id: string;
  name: string;
  color: string;
  properties: NodePropertiesDto;
  position: {
    x: number;
    y: number;
  };
}

export interface GptEdge {
  id: string;
  name: string;
  from: string;
  to: string;
  color: string;
}

export interface IFrameCurrentMapList {
  [uuid: string]: GraphMap;
}
